// client/src/pages/admin/AdminApprovals.js
// ============================================
// ADMIN APPROVALS PAGE
// Manage pending business and wholesaler registrations
// ============================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { format } from 'date-fns';

const AdminApprovals = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [pendingAccounts, setPendingAccounts] = useState({ businesses: [], wholesalers: [] });
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'business', 'wholesaler'
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Check admin access
  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [user, navigate]);

  // Fetch pending accounts and stats
  const fetchData = async () => {
    try {
      setLoading(true);
      const [pendingRes, statsRes] = await Promise.all([
        adminAPI.getPendingApprovals(filter),
        adminAPI.getApprovalStats()
      ]);
      
      setPendingAccounts({
        businesses: pendingRes.data.businesses || [],
        wholesalers: pendingRes.data.wholesalers || []
      });
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching approvals:', err);
      setError('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  // Handle approve
  const handleApprove = async (type, id) => {
    try {
      setActionLoading(true);
      setError('');
      
      await adminAPI.approveAccount(type, id);
      
      setSuccess(`Account approved successfully! User will receive an email notification.`);
      setShowDetailModal(false);
      setSelectedAccount(null);
      
      // Refresh data
      await fetchData();
      
      // Clear success after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error approving account:', err);
      setError(err.response?.data?.message || 'Failed to approve account');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle reject
  const handleReject = async () => {
    if (!rejectionReason || rejectionReason.trim().length < 10) {
      setError('Please provide a rejection reason (minimum 10 characters)');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      
      await adminAPI.rejectAccount(selectedAccount.type, selectedAccount._id, rejectionReason);
      
      setSuccess(`Account rejected. User will receive an email with the reason.`);
      setShowRejectModal(false);
      setShowDetailModal(false);
      setSelectedAccount(null);
      setRejectionReason('');
      
      // Refresh data
      await fetchData();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error rejecting account:', err);
      setError(err.response?.data?.message || 'Failed to reject account');
    } finally {
      setActionLoading(false);
    }
  };

  // Open detail modal
  const openDetail = (account, type) => {
    setSelectedAccount({ ...account, type });
    setShowDetailModal(true);
    setError('');
  };

  // Open reject modal
  const openRejectModal = () => {
    setShowRejectModal(true);
    setRejectionReason('');
    setError('');
  };

  // Get all accounts based on filter
  const getAllAccounts = () => {
    let accounts = [];
    
    if (filter === 'all' || filter === 'business') {
      accounts = [...accounts, ...pendingAccounts.businesses.map(b => ({ ...b, type: 'business' }))];
    }
    if (filter === 'all' || filter === 'wholesaler') {
      accounts = [...accounts, ...pendingAccounts.wholesalers.map(w => ({ ...w, type: 'wholesaler' }))];
    }
    
    // Sort by creation date (newest first)
    return accounts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const allAccounts = getAllAccounts();

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-indigo-900 shadow-lg">
        <div className="container mx-auto px-6 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center">
                ✅ Pending Approvals
              </h1>
              <p className="text-blue-200 mt-1">Review and approve new registrations</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-white hover:text-blue-200 transition-colors"
              >
                ← Back to Dashboard
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg flex items-center">
            <span className="mr-2">✅</span>
            {success}
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center">
            <span className="mr-2">❌</span>
            {error}
            <button onClick={() => setError('')} className="ml-auto font-bold">×</button>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Total Pending"
              value={stats.total?.pending || 0}
              icon="⏳"
              color="yellow"
              subtitle="Awaiting review"
            />
            <StatCard
              title="Pending Businesses"
              value={stats.businesses?.pending || 0}
              icon="🏪"
              color="blue"
              subtitle="Business registrations"
            />
            <StatCard
              title="Pending Wholesalers"
              value={stats.wholesalers?.pending || 0}
              icon="🏭"
              color="purple"
              subtitle="Wholesaler registrations"
            />
            <StatCard
              title="Total Approved"
              value={(stats.businesses?.approved || 0) + (stats.wholesalers?.approved || 0)}
              icon="✅"
              color="green"
              subtitle="All time"
            />
          </div>
        )}

        {/* Filter Tabs */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex space-x-4">
            <FilterTab
              label="All Pending"
              count={pendingAccounts.businesses.length + pendingAccounts.wholesalers.length}
              active={filter === 'all'}
              onClick={() => setFilter('all')}
            />
            <FilterTab
              label="Businesses"
              count={pendingAccounts.businesses.length}
              active={filter === 'business'}
              onClick={() => setFilter('business')}
            />
            <FilterTab
              label="Wholesalers"
              count={pendingAccounts.wholesalers.length}
              active={filter === 'wholesaler'}
              onClick={() => setFilter('wholesaler')}
            />
          </div>
        </div>

        {/* Pending Accounts List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">
              Pending Registrations ({allAccounts.length})
            </h2>
          </div>

          {allAccounts.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Pending Approvals</h3>
              <p className="text-gray-500">All registration requests have been reviewed.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {allAccounts.map((account) => (
                <AccountRow
                  key={`${account.type}-${account._id}`}
                  account={account}
                  onView={() => openDetail(account, account.type)}
                  onApprove={() => handleApprove(account.type, account._id)}
                  actionLoading={actionLoading}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedAccount && (
        <DetailModal
          account={selectedAccount}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedAccount(null);
          }}
          onApprove={() => handleApprove(selectedAccount.type, selectedAccount._id)}
          onReject={openRejectModal}
          loading={actionLoading}
        />
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedAccount && (
        <RejectModal
          account={selectedAccount}
          reason={rejectionReason}
          setReason={setRejectionReason}
          onConfirm={handleReject}
          onCancel={() => setShowRejectModal(false)}
          loading={actionLoading}
          error={error}
        />
      )}
    </div>
  );
};

// ============================================
// HELPER COMPONENTS
// ============================================

const StatCard = ({ title, value, icon, color, subtitle }) => {
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    yellow: 'from-yellow-500 to-yellow-600',
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
      <div className={`bg-gradient-to-r ${colors[color] || colors.blue} p-4`}>
        <div className="text-white text-3xl mb-2">{icon}</div>
        <div className="text-white">
          <p className="text-sm opacity-90">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
        </div>
      </div>
      <div className="p-3 bg-gray-50">
        <p className="text-xs text-gray-600">{subtitle}</p>
      </div>
    </div>
  );
};

const FilterTab = ({ label, count, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
      active
        ? 'bg-blue-600 text-white'
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`}
  >
    {label}
    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
      active ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'
    }`}>
      {count}
    </span>
  </button>
);

const AccountRow = ({ account, onView, onApprove, actionLoading }) => {
  const name = account.companyName || account.businessName || account.user?.name || 'Unknown';
  const email = account.user?.email || account.contact?.email || 'No email';
  
  return (
    <div className="px-6 py-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
            account.type === 'business' ? 'bg-blue-500' : 'bg-purple-500'
          }`}>
            {account.type === 'business' ? '🏪' : '🏭'}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{name}</h3>
            <p className="text-sm text-gray-500">{email}</p>
            <div className="flex items-center space-x-2 mt-1">
              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                account.type === 'business' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-purple-100 text-purple-700'
              }`}>
                {account.type === 'business' ? 'Business' : 'Wholesaler'}
              </span>
              {account.vatNumber && (
                <span className="text-xs text-gray-500">
                  VAT: {account.vatNumber}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="text-right mr-4">
            <p className="text-xs text-gray-500">Registered</p>
            <p className="text-sm font-medium text-gray-700">
              {account.createdAt ? format(new Date(account.createdAt), 'MMM d, yyyy') : 'N/A'}
            </p>
          </div>
          
          <button
            onClick={onView}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
          >
            View Details
          </button>
          
          <button
            onClick={onApprove}
            disabled={actionLoading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
          >
            {actionLoading ? '...' : '✓ Approve'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DetailModal = ({ account, onClose, onApprove, onReject, loading }) => {
  const name = account.companyName || account.businessName || account.user?.name || 'Unknown';
  const email = account.user?.email || account.contact?.email || 'No email';
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`p-6 ${account.type === 'business' ? 'bg-blue-600' : 'bg-purple-600'} text-white rounded-t-xl`}>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm opacity-80 uppercase tracking-wide">
                {account.type === 'business' ? 'Business Registration' : 'Wholesaler Registration'}
              </p>
              <h2 className="text-2xl font-bold mt-1">{name}</h2>
              <p className="text-sm opacity-80 mt-1">{email}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Company Info */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
              <span className="mr-2">🏢</span> Company Information
            </h3>
            <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
              <InfoRow label="Company Name" value={name} />
              <InfoRow label="Legal Name" value={account.legalName || 'Not provided'} />
              <InfoRow label="VAT Number" value={account.vatNumber || 'Not provided'} highlight />
              <InfoRow label="Tax Office" value={account.taxOffice || 'Not provided'} />
              <InfoRow label="Email" value={email} />
              <InfoRow label="Phone" value={account.contact?.phone || account.phone || 'Not provided'} />
            </div>
          </div>

          {/* Address */}
          {account.address && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <span className="mr-2">📍</span> Address
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700">
                  {account.address.street && `${account.address.street}, `}
                  {account.address.city && `${account.address.city}, `}
                  {account.address.state && `${account.address.state} `}
                  {account.address.postalCode && account.address.postalCode}
                </p>
                {account.address.country && (
                  <p className="text-gray-500 text-sm mt-1">{account.address.country}</p>
                )}
              </div>
            </div>
          )}

          {/* Registration Date */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
              <span className="mr-2">📅</span> Registration Details
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <InfoRow 
                label="Registered On" 
                value={account.createdAt ? format(new Date(account.createdAt), 'MMMM d, yyyy \'at\' h:mm a') : 'Unknown'} 
              />
              <InfoRow label="Account Status" value={account.accountStatus || 'pending'} highlight />
            </div>
          </div>

          {/* Wholesaler-specific info */}
          {account.type === 'wholesaler' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <span className="mr-2">📦</span> Wholesaler Details
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <InfoRow label="Description" value={account.description || 'Not provided'} />
                {account.serviceAreas && account.serviceAreas.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">Service Areas:</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {account.serviceAreas.map((area, i) => (
                        <span key={i} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="border-t border-gray-200 p-6 bg-gray-50 rounded-b-xl flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Close
          </button>
          <button
            onClick={onReject}
            disabled={loading}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
          >
            ✕ Reject
          </button>
          <button
            onClick={onApprove}
            disabled={loading}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50"
          >
            {loading ? 'Processing...' : '✓ Approve Account'}
          </button>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value, highlight }) => (
  <div className="py-1">
    <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
    <p className={`font-medium ${highlight ? 'text-blue-600' : 'text-gray-800'}`}>
      {value || '-'}
    </p>
  </div>
);

const RejectModal = ({ account, reason, setReason, onConfirm, onCancel, loading, error }) => {
  const name = account.companyName || account.businessName || account.user?.name || 'Unknown';
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Reject Registration</h2>
          <p className="text-gray-600 mt-1">
            You are about to reject the registration for <strong>{name}</strong>.
          </p>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}
          
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Rejection Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder="Please provide a reason for rejection (minimum 10 characters). This will be sent to the user via email."
            className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-red-500 focus:border-red-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            {reason.length}/10 characters minimum
          </p>

          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              ⚠️ The user will receive an email notification with this rejection reason.
            </p>
          </div>
        </div>

        <div className="border-t border-gray-200 p-6 flex justify-end space-x-4">
          <button
            onClick={onCancel}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading || reason.trim().length < 10}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
          >
            {loading ? 'Rejecting...' : 'Confirm Rejection'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminApprovals;